import React from 'react';
import { Plus, RotateCcw, Square, Ruler, Target, Printer, Layers, Sigma, MousePointer2, ChevronDown, PanelLeft } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';
import type { TableMainToolbarProps } from './TableMainToolbarDesktop';
import { useLanguage } from '@/app/contexts/LanguageContext';

type BorderPreset = 
  | 'outer-thick' 
  | 'all-borders' 
  | 'no-borders' 
  | 'horizontal-only' 
  | 'vertical-only'
  | 'inner-grid';

export function TableMainToolbarMobile(props: TableMainToolbarProps) {
  const { t } = useLanguage();
  const {
    tableConfig,
    headerCellSelection,
    onMergeHeaderCells,
    onSplitHeaderCells,
    onClearSelection,
    onAddColumn,
    onAddHeaderRow,
    onResetHeaderRows,
    onApplyBorderPreset,
    onOpenBorderDialog,
    onOpenPrintDialog,
    onOpenGroupByPanel,
    onToggleTotalsRow,
    onToggleSidebar,
    isSidebarVisible,
  } = props;

  const hasSelection = headerCellSelection.length > 0;
  const hasMultipleSelected = headerCellSelection.length >= 2;
  const hasSingleSelected = headerCellSelection.length === 1;

  const borderPresets: Array<{ id: BorderPreset; label: string; icon: string }> = [
    { id: 'outer-thick', label: t('table.toolbar.borderPresetsNames.outerThick'), icon: '⬜' },
    { id: 'all-borders', label: t('table.toolbar.borderPresetsNames.allBorders'), icon: '⊞' },
    { id: 'no-borders', label: t('table.toolbar.borderPresetsNames.noBorders'), icon: '⊟' },
    { id: 'horizontal-only', label: t('table.toolbar.borderPresetsNames.horizontalOnly'), icon: '☰' },
    { id: 'vertical-only', label: t('table.toolbar.borderPresetsNames.verticalOnly'), icon: '☱' },
    { id: 'inner-grid', label: t('table.toolbar.borderPresetsNames.innerGrid'), icon: '⊡' },
  ];

  const lineWidths = [
    { value: 1, label: `${t('table.toolbar.thickness')} 1px` },
    { value: 2, label: `${t('table.toolbar.thickness')} 2px`, recommended: true },
    { value: 3, label: `${t('table.toolbar.thickness')} 3px` },
    { value: 4, label: `${t('table.toolbar.thickness')} 4px` },
  ];

  // Helper for button styles
  const getButtonStyle = (active: boolean, disabled: boolean) => ({
    backgroundColor: active ? 'var(--md-secondary)' : 'var(--md-surface-container-high)',
    color: active ? 'var(--md-on-secondary)' : 'var(--md-on-surface)',
    opacity: disabled ? 0.4 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: active ? 'none' : '1px solid var(--md-outline)',
  });

  const primaryButtonStyle = {
    backgroundColor: 'var(--md-primary)',
    color: 'var(--md-on-primary)',
  };

  return (
    <TooltipProvider>
      <div 
        className="flex 2xl:hidden flex-col border-b"
        style={{
          backgroundColor: 'var(--md-surface-container)',
          borderColor: 'var(--md-outline)',
        }}
      >
        {/* Scrollable Toolbar Area */}
        <div className="flex items-center gap-2 px-2 py-2 overflow-x-auto no-scrollbar touch-pan-x">
          
          {/* Sidebar Toggle */}
          {onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className="p-2 rounded transition-colors inline-flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: isSidebarVisible ? 'var(--md-secondary-container)' : 'transparent',
                color: isSidebarVisible ? 'var(--md-on-secondary-container)' : 'var(--md-on-surface)',
                border: '1px solid var(--md-outline)',
              }}
            >
              <PanelLeft className="w-5 h-5" />
            </button>
          )}

          {/* Group 1: Cell Operations */}
          <div className="flex items-center gap-1 shrink-0">
            <Tooltip>
              <TooltipTrigger
                onClick={onMergeHeaderCells}
                disabled={!hasMultipleSelected}
                className="p-2 rounded transition-colors inline-flex items-center justify-center"
                style={getButtonStyle(hasMultipleSelected, !hasMultipleSelected)}
              >
                <MousePointer2 className="w-5 h-5" />
                <span className="sr-only">{t('table.toolbar.merge')}</span>
              </TooltipTrigger>
              <TooltipContent>{t('table.cells.minTwo')}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger
                onClick={onSplitHeaderCells}
                disabled={!hasSingleSelected}
                className="p-2 rounded transition-colors inline-flex items-center justify-center"
                style={getButtonStyle(hasSingleSelected, !hasSingleSelected)}
              >
                <div className="relative">
                    <MousePointer2 className="w-5 h-5" />
                    <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-red-500 rounded-full border border-white" />
                </div>
                <span className="sr-only">{t('table.toolbar.split')}</span>
              </TooltipTrigger>
              <TooltipContent>{t('table.toolbar.split')}</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-6 bg-[var(--md-outline-variant)] shrink-0 mx-1" />

          {/* Group 2: Borders */}
          <div className="flex items-center gap-1 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={!hasSelection}
                className="flex items-center gap-1 px-2 py-1.5 rounded text-sm font-medium"
                style={getButtonStyle(false, !hasSelection)}
              >
                <Square className="w-5 h-5" />
                <ChevronDown className="w-3 h-3 opacity-50" />
                <span className="sr-only">{t('table.toolbar.borderPresets')}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {borderPresets.map((preset) => (
                  <DropdownMenuItem 
                    key={preset.id} 
                    onSelect={() => onApplyBorderPreset(preset.id)}
                    className="gap-2"
                  >
                    <span className="text-lg">{preset.icon}</span>
                    <span>{preset.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={!hasSelection}
                className="flex items-center gap-1 px-2 py-1.5 rounded text-sm font-medium"
                style={getButtonStyle(false, !hasSelection)}
              >
                <Ruler className="w-5 h-5" />
                <ChevronDown className="w-3 h-3 opacity-50" />
                <span className="sr-only">{t('table.toolbar.lineWidth')}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {lineWidths.map((width) => (
                  <DropdownMenuItem 
                    key={width.value} 
                    onSelect={() => onApplyBorderPreset(`width-${width.value}`)}
                    className="gap-2"
                  >
                    <div 
                      style={{ 
                        width: '24px', 
                        height: `${width.value}px`, 
                        backgroundColor: 'currentColor',
                        borderRadius: '1px',
                      }} 
                    />
                    <span>{width.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Tooltip>
              <TooltipTrigger
                onClick={onOpenBorderDialog}
                className="p-2 rounded transition-colors inline-flex items-center justify-center"
                style={{
                  ...primaryButtonStyle,
                  opacity: 1,
                }}
              >
                <Target className="w-5 h-5" />
                <span className="sr-only">{t('table.toolbar.editBorders')}</span>
              </TooltipTrigger>
              <TooltipContent>{t('table.toolbar.editBorders')}</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-6 bg-[var(--md-outline-variant)] shrink-0 mx-1" />

          {/* Group 3: Table Structure */}
          <div className="flex items-center gap-1 shrink-0">
              {onAddColumn && (
              <Tooltip>
                <TooltipTrigger
                  onClick={onAddColumn}
                  className="flex items-center gap-1 px-2 py-1.5 rounded text-sm font-medium"
                  style={getButtonStyle(false, false)}
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('table.toolbar.column')}</span>
                </TooltipTrigger>
                <TooltipContent>{t('table.sidebar.createAttribute')}</TooltipContent>
              </Tooltip>
            )}
            
            <Tooltip>
              <TooltipTrigger
                onClick={onAddHeaderRow}
                className="flex items-center gap-1 px-2 py-1.5 rounded text-sm font-medium"
                style={getButtonStyle(false, false)}
              >
                <Plus className="w-4 h-4" />
                <span>{t('table.toolbar.row')}</span>
              </TooltipTrigger>
              <TooltipContent>{t('table.grid.addHeaderRow')}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger
                onClick={onResetHeaderRows}
                className="p-2 rounded transition-colors inline-flex items-center justify-center"
                style={getButtonStyle(false, false)}
              >
                <RotateCcw className="w-5 h-5" />
                <span className="sr-only">{t('common.reset')}</span>
              </TooltipTrigger>
              <TooltipContent>{t('table.toolbar.resetHeaders')}</TooltipContent>
            </Tooltip>
          </div>

          <div className="w-px h-6 bg-[var(--md-outline-variant)] shrink-0 mx-1" />

          {/* Group 4: Export & Data */}
          <div className="flex items-center gap-1 shrink-0">
              {onOpenPrintDialog && (
                <Tooltip>
                  <TooltipTrigger
                    onClick={onOpenPrintDialog}
                    className="p-2 rounded transition-colors inline-flex items-center justify-center"
                    style={{
                      backgroundColor: 'var(--md-secondary)',
                      color: 'var(--md-on-secondary)',
                    }}
                  >
                    <Printer className="w-5 h-5" />
                    <span className="sr-only">{t('common.print')}</span>
                  </TooltipTrigger>
                  <TooltipContent>{t('common.print')}</TooltipContent>
                </Tooltip>
              )}

              {onOpenGroupByPanel && (
                <>
                  <Tooltip>
                    <TooltipTrigger
                      onClick={onOpenGroupByPanel}
                      className="p-2 rounded transition-colors relative inline-flex items-center justify-center"
                      style={{
                        backgroundColor: tableConfig.groupConfig?.enabled ? 'var(--md-tertiary)' : 'var(--md-surface-container-high)',
                        color: tableConfig.groupConfig?.enabled ? 'var(--md-on-tertiary)' : 'var(--md-on-surface)',
                        border: '1px solid var(--md-outline)',
                      }}
                    >
                      <Layers className="w-5 h-5" />
                      {tableConfig.groupConfig?.enabled && tableConfig.groupConfig.columnIds.length > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                          {tableConfig.groupConfig.columnIds.length}
                        </span>
                      )}
                      <span className="sr-only">{t('table.toolbar.group')}</span>
                    </TooltipTrigger>
                    <TooltipContent>{t('table.toolbar.group')}</TooltipContent>
                  </Tooltip>

                  {onToggleTotalsRow && (
                    <Tooltip>
                      <TooltipTrigger
                        onClick={onToggleTotalsRow}
                        className="p-2 rounded transition-colors inline-flex items-center justify-center"
                        style={{
                          backgroundColor: tableConfig.totalsRowConfig?.enabled ? 'var(--md-tertiary)' : 'var(--md-surface-container-high)',
                          color: tableConfig.totalsRowConfig?.enabled ? 'var(--md-on-tertiary)' : 'var(--md-on-surface)',
                          border: '1px solid var(--md-outline)',
                        }}
                      >
                        <Sigma className="w-5 h-5" />
                        <span className="sr-only">{t('table.toolbar.totalsRow')}</span>
                      </TooltipTrigger>
                      <TooltipContent>{t('table.toolbar.totalsRow')}</TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
          </div>

          {/* Spacer */}
          <div className="flex-1 min-w-4" />

          {/* Selection Clear (Icon only) */}
            {hasSelection && (
              <Tooltip>
                <TooltipTrigger
                  onClick={onClearSelection}
                  className="p-2 rounded transition-colors hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 inline-flex items-center justify-center"
                >
                  <span className="text-xs font-bold mr-1">{headerCellSelection.length}</span>
                  <span className="sr-only">{t('table.toolbar.clearSelection')}</span>
                  <span className="text-xs">✕</span>
                </TooltipTrigger>
                <TooltipContent>{t('table.toolbar.clearSelection')}</TooltipContent>
              </Tooltip>
            )}
        </div>

        {/* Compact Stats Footer */}
        <div 
          className="flex items-center justify-between px-2 py-1 text-[10px] border-t"
          style={{
            backgroundColor: 'var(--md-surface-container-low)',
            borderColor: 'var(--md-outline-variant)',
            color: 'var(--md-on-surface-variant)',
          }}
        >
            <div className="flex items-center gap-2 overflow-hidden whitespace-nowrap text-ellipsis">
              <span>{tableConfig.rows.filter(r => !r.isHeader).length} {t('table.toolbar.rows')}</span>
              <span>•</span>
              <span>{tableConfig.columns.length} {t('table.toolbar.columns')}</span>
            </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
